// Programme Dashboard rendering engine.
// Mounted into a pre-existing DOM container by initDashboard().

import { createToast, type ToastHandle, type ToastKind, type ToastOptions } from './toast';
import { html, jsArg, raw, setHtml, type SafeHtml } from './html';

// Thrown by onSave so the save loop can tell transient failures (retried)
// from fatal ones like an expired session (surfaced immediately).
export class SaveError extends Error {
  retryable: boolean;
  constructor(message: string, retryable: boolean) {
    super(message);
    this.retryable = retryable;
  }
}

export interface DashboardMeta {
  rag: 'green' | 'amber' | 'red';
  narrative: string;
  focus: string;
  updatedAt?: string;
}

export interface DashboardProject {
  id: string;
  country: string;
  brand: string;
  model: string;
  status: 'signoff' | 'tuning' | 'onboarding' | 'live';
  liveDate: string;
  users: string | number;
  coaching: string;
  conversations: string | number;
  totalConversations: number;
  active: string;
  trend: number[];
  targetConvsPerUserPerWeek?: number;
  leadName?: string;
  leadRole?: string;
  clientLead?: string;
  lastTouch?: string;
  focus?: string;
  kaiProjectId?: string;
  activeUsers?: number;
}

export interface DashboardAction {
  id: string;
  ws: string;
  text: string;
  details: string;
  owner: string;
  dueType: 'date' | 'ongoing' | 'decision' | 'tbc' | 'soon' | 'upcoming';
  dueDate: string;
  due: string;
  linkedId: string;
  projectId: string;
  archived: boolean;
  archivedAt: string | null;
}

export interface DashboardReport {
  id: string;
  name: string;
  freq: string;
  audience: string;
  date: string;
  projectId: string;
  archived: boolean;
  archivedAt: string | null;
}

export interface DashboardDecision {
  id: string;
  name: string;
  text: string;
  mitigation: string;
  type: 'decision' | 'risk';
  projectId: string;
  archived: boolean;
  archivedAt: string | null;
}

export interface DashboardContent {
  meta: DashboardMeta;
  projects: DashboardProject[];
  workstreams: string[];
  actions: DashboardAction[];
  reports: DashboardReport[];
  decisions: DashboardDecision[];
}

export const DEFAULT_DASHBOARD_CONTENT: DashboardContent = {
  meta: { rag: 'green', narrative: '', focus: '' },
  workstreams: ['Tech', 'Insights', 'Strategy', 'Project Mgmt'],
  projects: [],
  actions: [],
  reports: [],
  decisions: [],
};

export function isDashboardFormat(content: unknown): content is DashboardContent {
  if (!content || typeof content !== 'object') return false;
  const c = content as Record<string, unknown>;
  // Checks everything renderAll() reads at the top level, so content edited as
  // raw JSON can't pass with a shape that crashes the renderer.
  const meta = c.meta as Record<string, unknown> | null | undefined;
  return !!meta && typeof meta === 'object' && Object.prototype.hasOwnProperty.call(RAG_CONFIG, meta.rag as string)
    && ['projects', 'actions', 'workstreams', 'reports', 'decisions'].every(k => Array.isArray(c[k]));
}

// ─── Rendering engine ───────────────────────────────────────────────────────

interface DashboardOptions {
  prefix: string;        // DOM id prefix, e.g. 'pd'
  clientId: string;
  onSave: (data: DashboardContent) => Promise<void>;
  // Called when a data fetch gets a 401 — the server has ended the session.
  onSessionExpired: () => void;
  editAllowed: boolean;
}

export interface DashboardController {
  setData: (data: DashboardContent) => void;
  setEditMode: (enabled: boolean) => void;
  getEditMode: () => boolean;
  getData: () => DashboardContent;
}

const RAG_CONFIG = {
  green: { label: 'On track', pillCls: 'pd-pill-g', dotCls: 'pd-dot-g', btnCls: 'pd-rag-btn-g' },
  amber: { label: 'At risk', pillCls: 'pd-pill-a', dotCls: 'pd-dot-a', btnCls: 'pd-rag-btn-a' },
  red: { label: 'Off track', pillCls: 'pd-pill-r', dotCls: 'pd-dot-r', btnCls: 'pd-rag-btn-r' },
};

const STATUS_BADGES: Record<string, SafeHtml> = {
  live: raw('<span class="pd-badge pd-b-live">Live</span>'),
  onboarding: raw('<span class="pd-badge pd-b-onboarding">Onboarding</span>'),
  tuning: raw('<span class="pd-badge pd-b-tuning">Tuning</span>'),
  signoff: raw('<span class="pd-badge pd-b-signoff">Sign Off</span>'),
};

function uid(): string {
  return 'x' + Math.random().toString(36).slice(2, 11);
}

function langToCountry(lang: string): string {
  const parts = lang.split('-');
  const region = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  if (!region) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(region) || region;
  } catch {
    return region;
  }
}

function projLabel(data: DashboardContent, id: string): string {
  const p = data.projects.find(x => x.id === id);
  return p ? `${p.brand} (${p.country})` : '';
}

function fmtDate(iso: string): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function sparkline(data: number[], w = 70, h = 22): SafeHtml {
  if (!data || !data.length) return html``;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const stepX = w / ((data.length - 1) || 1);
  const pts = data.map((v, i) => `${(i * stepX).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`).join(' ');
  const last = data[data.length - 1], first = data[0];
  const color = last >= first ? 'var(--color-success)' : 'var(--color-danger)';
  const lastX = (data.length - 1) * stepX;
  const lastY = h - ((last - min) / range) * h;
  return html`<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none"><polyline fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" points="${pts}"/><circle cx="${lastX.toFixed(1)}" cy="${lastY.toFixed(1)}" r="2" fill="${color}"/></svg>`;
}

function lineChart(data: number[]): SafeHtml {
  const w = 600, h = 160, pl = 32, pr = 12, pt = 12, pb = 22;
  const cw = w - pl - pr, ch = h - pt - pb;
  const max = Math.max(...data, 1);
  const stepX = cw / ((data.length - 1) || 1);
  const pts = data.map((v, i) => [pl + i * stepX, pt + ch - (v / max) * ch]);
  const path = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = path + ` L ${pts[pts.length - 1][0].toFixed(1)} ${(pt + ch).toFixed(1)} L ${pts[0][0].toFixed(1)} ${(pt + ch).toFixed(1)} Z`;
  const ticks = [0, Math.round(max / 2), max];
  const grid = ticks.map(t => {
    const y = pt + ch - (t / max) * ch;
    return html`<line x1="${pl}" y1="${y}" x2="${pl + cw}" y2="${y}" stroke="var(--color-edge-light)" stroke-width="1"/><text x="${pl - 6}" y="${y + 3}" font-size="9" fill="var(--color-subtle)" text-anchor="end">${t}</text>`;
  });
  const dots = pts.map((p, i) => i === pts.length - 1 && html`<circle cx="${p[0]}" cy="${p[1]}" r="3.5" fill="var(--color-primary)" stroke="white" stroke-width="2"/>`);
  return html`<svg width="100%" height="${h}" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">
    <defs><linearGradient id="pdg1" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="var(--color-primary)" stop-opacity=".18"/><stop offset="100%" stop-color="var(--color-primary)" stop-opacity="0"/></linearGradient></defs>
    ${grid}
    <path d="${area}" fill="url(#pdg1)"/>
    <path d="${path}" fill="none" stroke="var(--color-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    ${dots}
    <text x="${pl}" y="${h - 4}" font-size="9" fill="var(--color-subtle)">15 days ago</text>
    <text x="${pl + cw}" y="${h - 4}" font-size="9" fill="var(--color-subtle)" text-anchor="end">today</text>
  </svg>`;
}

function computeDueDisplay(a: DashboardAction): { cls: string; label: string; title?: string } {
  if (a.dueType === 'date' && a.dueDate) {
    const d = new Date(a.dueDate);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
    let cls = 'pd-due-ok';
    if (diff < 0) cls = 'pd-due-late';
    else if (diff <= 3) cls = 'pd-due-soon';
    const label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + (diff < 0 ? ' (overdue)' : diff === 0 ? ' (today)' : '');
    return { cls, label, title: diff < 0 ? `${-diff} day${diff === -1 ? '' : 's'} overdue` : `${diff} day${diff === 1 ? '' : 's'} away` };
  }
  const labels: Record<string, string> = { ongoing: 'Ongoing', decision: 'Decision req.', tbc: 'TBC', soon: 'Soon', upcoming: 'Upcoming' };
  return { cls: `pd-due-${a.dueType || 'tbc'}`, label: a.due || labels[a.dueType] || '—' };
}

function buildFilterDropdown(data: DashboardContent, activeIds: string[], section: string, prefix: string): SafeHtml {
  const count = activeIds.length;
  const btnLabel = count === 0 ? 'All projects'
    : count === 1 ? (() => { const proj = data.projects.find(pp => pp.id === activeIds[0]); return proj ? `${proj.brand} (${proj.country})` : '1 project'; })()
      : `${count} projects`;
  return html`<span class="text-2xs text-subtle font-semibold uppercase tracking-label">Filter</span>
    <div style="position:relative;display:inline-block">
      <button class="pd-filter-chip${count > 0 ? ' active' : ''}" style="gap:5px"
        onclick="event.stopPropagation();window.__pd.openSectionFilter('${section}',event)">
        <span>${btnLabel}</span>
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M19 9l-7 7-7-7"/></svg>
      </button>
      <div class="pd-fdrop-pop" id="${prefix}-fpop-${section}" onclick="event.stopPropagation()">
        <div class="pd-pop-list">
          <label class="pd-pop-opt"><input type="checkbox" ${count === 0 ? 'checked' : ''}
            onchange="window.__pd.clearSectionFilter('${section}')"/> All projects</label>
          ${data.projects.slice().sort((a, b) => a.brand.localeCompare(b.brand)).map(proj =>
    html`<label class="pd-pop-opt"><input type="checkbox" value="${proj.id}" ${activeIds.includes(proj.id) ? 'checked' : ''}
              onchange="window.__pd.toggleSectionFilter('${section}',${jsArg(proj.id)})"/>
              ${proj.brand} (${proj.country})</label>`)}
        </div>
      </div>
    </div>`;
}

export function initDashboard(prefix: string, initialData: DashboardContent, opts: DashboardOptions): DashboardController {
  let DATA: DashboardContent = JSON.parse(JSON.stringify(initialData));
  let kaiProjects: import('../shared/types').Project[] = [];
  let projSortKey: string | null = null;
  let projSortDir: 'asc' | 'desc' = 'asc';
  let projFilters: Record<string, string | string[]> = {};
  let projFilterOpen: string | null = null;
  let projPage = 0;
  let projPageSize = 10;
  let actionPage = 0;
  let actionPageSize = 10;
  let reportPage = 0;
  let reportPageSize = 10;
  let decisionPage = 0;
  let decisionPageSize = 10;
  let modalDirty = false;

  // Pre-fetch KAI projects for the "Add project" modal
  fetch(`/api/projects?clientId=${encodeURIComponent(opts.clientId)}`, { credentials: 'include' })
    .then(r => {
      if (r.status === 401) { opts.onSessionExpired(); return null; }
      return r.ok ? r.json() : null;
    })
    .then(d => { if (d?.projects) kaiProjects = d.projects.filter((p: import('../shared/types').Project) => !p.archived); })
    .catch(() => { });

  function applyMetrics(pp: DashboardProject, metrics: { conversations: number; activeUsers: number; totalConversations: number; dailyCounts: number[] }) {
    pp.conversations = metrics.conversations;
    pp.totalConversations = metrics.totalConversations;
    pp.trend = metrics.dailyCounts;
    pp.activeUsers = metrics.activeUsers;
    const userCount = parseFloat(String(pp.users));
    pp.active = (userCount > 0)
      ? `${Math.round((metrics.activeUsers / userCount) * 100)}%`
      : '';
  }

  function fetchMetrics(projects: DashboardProject[], andRender = true) {
    const linked = projects.filter(pp => pp.kaiProjectId);
    if (linked.length === 0) return;
    Promise.all(linked.map(pp =>
      fetch(`/api/conversations/metrics?project_id=${encodeURIComponent(pp.kaiProjectId!)}`, { credentials: 'include' })
        .then(r => {
          if (r.status === 401) { opts.onSessionExpired(); return null; }
          if (!r.ok) { console.error('[dashboard] metrics fetch failed', r.status, pp.kaiProjectId); return null; }
          return r.json();
        })
        .then((metrics: { conversations: number; activeUsers: number; totalConversations: number; dailyCounts: number[] } | null) => {
          if (metrics) applyMetrics(pp, metrics);
        })
        .catch((e) => console.error('[dashboard] metrics error', pp.kaiProjectId, e))
    )).then(() => { if (andRender) renderAll(); });
  }

  // Fetch live metrics for all linked projects on load
  fetchMetrics(DATA.projects);
  let editMode = false;
  let activeWS = 0;
  let actionFilters: string[] = [];
  let reportFilters: string[] = [];
  let decisionFilters: string[] = [];
  let filterDropOpen: string | null = null;
  let showArchivedActions = false;
  let showArchivedReports = false;
  let showArchivedDecisions = false;
  let modalCtx: string | null = null;
  let modalEditId: string | null = null;

  const p = (id: string) => document.getElementById(`${prefix}-${id}`);

  const MAX_SAVE_ATTEMPTS = 3;
  let saveChain: Promise<void> = Promise.resolve();
  let saveToast: ToastHandle | null = null;

  function setSaveToast(kind: ToastKind, message: string, toastOpts?: ToastOptions) {
    if (saveToast && !saveToast.isDismissed()) saveToast.set(kind, message, toastOpts);
    else saveToast = createToast(kind, message, toastOpts);
  }

  async function runSave(snapshot: DashboardContent): Promise<void> {
    setSaveToast('saving', 'Saving…');
    for (let attempt = 1; ; attempt++) {
      try {
        await opts.onSave(snapshot);
        setSaveToast('success', 'Saved');
        return;
      } catch (err) {
        const retryable = err instanceof SaveError ? err.retryable : true;
        if (retryable && attempt < MAX_SAVE_ATTEMPTS) {
          setSaveToast('saving', `Save failed — retrying (attempt ${attempt + 1} of ${MAX_SAVE_ATTEMPTS})…`);
          await new Promise(r => setTimeout(r, 1000 * attempt));
          continue;
        }
        const message = err instanceof SaveError
          ? err.message
          : 'Save failed — your last change was not stored.';
        setSaveToast('error', message, {
          actionLabel: 'Retry',
          // Re-snapshot on retry: DATA still holds the failed change, and may
          // hold newer edits that must not be clobbered by an old snapshot.
          onAction: () => save(),
        });
        return;
      }
    }
  }

  // Saves run one at a time, in order — overlapping PUTs could land
  // out of order and persist a stale snapshot.
  function queueSave(snapshot: DashboardContent) {
    saveChain = saveChain.then(() => runSave(snapshot));
  }

  function save() {
    if (!opts.editAllowed) return;
    DATA.meta.updatedAt = new Date().toISOString();
    queueSave(JSON.parse(JSON.stringify(DATA)));
  }

  // ── RAG ──────────────────────────────────────────────────────────────────
  function renderRAG() {
    const optsEl = p('rag-opts');
    const pillEl = p('rag-pill');
    const narrEl = p('narr') as HTMLInputElement | null;
    const focusEl = p('focus') as HTMLInputElement | null;
    if (!optsEl || !pillEl) return;

    const c = RAG_CONFIG[DATA.meta.rag];
    if (editMode) {
      optsEl.style.display = '';
      setHtml(optsEl, html`${(Object.entries(RAG_CONFIG) as [string, typeof RAG_CONFIG.green][]).map(([k, v]) =>
        html`<button class="pd-rag-btn${DATA.meta.rag === k ? ' ' + v.btnCls : ''}" onclick="window.__pd.setRAG('${k}')"><span>${v.label}</span></button>`
      )}`);
    } else {
      optsEl.style.display = 'none';
      setHtml(optsEl, html``);
    }
    pillEl.className = `pd-rag-pill ${c.pillCls}`;
    setHtml(pillEl, html`<span class="pd-rag-dot ${c.dotCls}"></span><span>${c.label}</span>`);

    if (narrEl) {
      narrEl.value = DATA.meta.narrative || '';
      narrEl.readOnly = !editMode;
      narrEl.placeholder = editMode ? 'Programme commentary — click to edit' : '';
      narrEl.onchange = editMode ? () => { DATA.meta.narrative = narrEl.value; save(); } : null;
    }
    if (focusEl) {
      focusEl.value = DATA.meta.focus || '';
      focusEl.readOnly = !editMode;
      focusEl.placeholder = editMode ? 'What is the programme focused on right now? — click to edit' : '';
      focusEl.onchange = editMode ? () => { DATA.meta.focus = focusEl.value; save(); } : null;
    }
  }

  // ── PROJECTS ──────────────────────────────────────────────────────────────
  const PROJ_COLS = [
    { k: 'country', l: 'Country' },
    { k: 'brand', l: 'Brand' },
    { k: 'model', l: 'Model' },
    { k: 'status', l: 'Status' },
    { k: 'liveDate', l: 'Live Date' },
    { k: 'users', l: 'Users', numeric: true },
    { k: 'coaching', l: 'Coaching %', numeric: true },
    { k: 'conversations', l: 'Convos (30d)', numeric: true },
    { k: 'attainment', l: 'Attainment', noSort: true, noFilter: true, numeric: true },
    { k: 'totalConversations', l: 'Total convos', numeric: true },
    { k: 'trend', l: 'Trend', noSort: true, noFilter: true },
    { k: 'wow', l: 'WoW', noSort: true, noFilter: true, numeric: true },
    { k: 'active', l: 'Active %', numeric: true },
  ] as const;

  const PROJ_COL_TITLES: Partial<Record<string, string>> = {
    attainment: 'Conversations in the last 30 days as a % of the monthly target (target/user/week × users × 30÷7)',
    wow: 'Week-on-week change: conversations in the last 7 days vs. the previous 7 days',
    active: 'Users who uploaded at least one conversation in the last 30 days, as a % of the enabled user count set on this project',
    conversations: 'Total conversations uploaded in the last 30 days',
    totalConversations: 'All conversations uploaded to this project (all time)',
  };

  function renderProjCell(proj: DashboardProject, k: string): SafeHtml {
    if (k === 'brand') return html`<td><span class="font-semibold text-ink">${proj.brand}</span></td>`;
    if (k === 'model') return html`<td class="capitalize">${proj.model ?? ''}</td>`;
    if (k === 'status') return html`<td>${STATUS_BADGES[proj.status] ?? proj.status}</td>`;
    if (k === 'liveDate') {
      const d = proj.liveDate && /^\d{4}-\d{2}-\d{2}$/.test(proj.liveDate) ? fmtDate(proj.liveDate) : '';
      return html`<td>${d}</td>`;
    }
    if (k === 'trend') return html`<td>${sparkline(proj.trend)}</td>`;
    if (k === 'totalConversations') return html`<td class="pd-mono">${proj.totalConversations || 0}</td>`;
    if (k === 'users' || k === 'conversations') return html`<td class="pd-mono">${(proj as unknown as Record<string, unknown>)[k]}</td>`;
    if (k === 'coaching') return html`<td class="text-right">${proj.coaching ?? ''}</td>`;
    if (k === 'attainment') {
      const target = proj.targetConvsPerUserPerWeek;
      const users = parseFloat(String(proj.users)) || 0;
      if (!target || !users) return html`<td class="text-right"></td>`;
      const expected = target * users * (30 / 7);
      const actual = parseFloat(String(proj.conversations)) || 0;
      const ratio = actual / expected;
      const pct = Math.round(ratio * 100);
      const color = ratio >= 0.9 ? 'var(--color-success)' : ratio >= 0.6 ? 'var(--color-warn)' : 'var(--color-danger)';
      return html`<td class="pd-mono" style="color:${color}" title="30-day attainment: ${target} convos/user/week × ${users} users × (30÷7 days) = ${Math.round(expected)} expected. Actual: ${actual} conversations.">${pct}%</td>`;
    }
    if (k === 'wow') {
      const t = proj.trend || [];
      if (t.length < 14) return html`<td class="text-right"></td>`;
      const last7 = t.slice(-7).reduce((a, b) => a + b, 0);
      const prev7 = t.slice(-14, -7).reduce((a, b) => a + b, 0);
      if (!prev7) return html`<td class="text-right"></td>`;
      const delta = Math.round(((last7 - prev7) / prev7) * 100);
      const color = delta >= 0 ? 'var(--color-success)' : 'var(--color-danger)';
      return html`<td class="pd-mono" style="color:${color}" title="Week-on-week: last 7 days (${last7}) vs. previous 7 days (${prev7})">${delta >= 0 ? '▲' : '▼'} ${Math.abs(delta)}%</td>`;
    }
    if (k === 'active') {
      const userCount = parseFloat(String(proj.users)) || 0;
      const activeTitle = proj.activeUsers != null && userCount > 0
        ? `${proj.activeUsers} of ${userCount} enabled users active in the last 30 days`
        : 'Users who uploaded at least one conversation in the last 30 days';
      const val = proj.active && proj.active !== '—' ? proj.active : '';
      return html`<td class="text-right" title="${activeTitle}">${val}</td>`;
    }
    return html`<td>${(proj as unknown as Record<string, unknown>)[k] ?? ''}</td>`;
  }

  function closeProjFilterPops() {
    document.querySelectorAll('.pd-col-pop').forEach(el => el.classList.remove('show'));
    projFilterOpen = null;
  }

  function renderSectionFooter(elId: string, total: number, page: number, pageSize: number, type: string) {
    const footerEl = p(elId);
    if (!footerEl) return;
    if (!total) { setHtml(footerEl, html``); footerEl.style.display = 'none'; return; }
    footerEl.style.display = '';
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const cur = Math.min(page, totalPages - 1);
    const start = cur * pageSize + 1;
    const end = Math.min((cur + 1) * pageSize, total);
    setHtml(footerEl, html`
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-subtle">Show</span>
        <select class="pd-pg-size-sel" onchange="window.__pd.setPageSize('${type}',parseInt(this.value))">
          ${[5, 10, 25, 50].map(n => html`<option value="${n}"${n === pageSize ? ' selected' : ''}>${n}</option>`)}
        </select>
        <span class="text-xs text-subtle">of ${total} · ${start}–${end}</span>
      </div>
      <div class="flex items-center gap-[3px]">
        <button class="pd-pg-btn" ${cur === 0 ? 'disabled' : ''} onclick="window.__pd.setPage('${type}',${cur - 1})">‹</button>
        ${Array.from({ length: totalPages }, (_, i) =>
      html`<button class="pd-pg-btn${i === cur ? ' active' : ''}" onclick="window.__pd.setPage('${type}',${i})">${i + 1}</button>`
    )}
        <button class="pd-pg-btn" ${cur >= totalPages - 1 ? 'disabled' : ''} onclick="window.__pd.setPage('${type}',${cur + 1})">›</button>
      </div>`);
  }

  function renderProjects() {
    const thead = p('proj-thead');
    const tbody = p('proj-tbody');
    if (!thead || !tbody) return;

    // Build header
    setHtml(thead, html`<tr>${PROJ_COLS.map(c => {
      const sorted = projSortKey === c.k;
      const arrow = sorted ? (projSortDir === 'asc' ? '▲' : '▼') : '↕';
      const hasFilter = projFilters[c.k] && (Array.isArray(projFilters[c.k]) ? (projFilters[c.k] as string[]).length > 0 : false);
      const noSort = (c as { noSort?: boolean }).noSort;
      const noFilter = (c as { noFilter?: boolean }).noFilter;
      const isNumeric = (c as { numeric?: boolean }).numeric;
      const colTitle = PROJ_COL_TITLES[c.k];
      return html`<th class="${sorted ? 'pd-sorted' : ''} ${hasFilter ? 'pd-has-filter' : ''} ${isNumeric ? 'text-right' : ''}"
        ${noSort ? '' : html`onclick="window.__pd.sortProj('${c.k}', event)"`}${colTitle ? html` title="${colTitle}"` : ''}>
        <span class="inline-flex items-center gap-1">
          ${colTitle ? html`<span class="pd-th-tip">${c.l}</span>` : c.l}
          ${noSort ? '' : html`<span class="pd-sort-icon">${arrow}</span>`}
          ${noFilter ? '' : html`<span class="pd-filt-icon" onclick="window.__pd.toggleProjFilter('${c.k}', event)" title="Filter">▾</span>`}
        </span>
        ${noFilter ? '' : html`<div class="pd-col-pop" id="${prefix}-fp-${c.k}" onclick="event.stopPropagation()"></div>`}
      </th>`;
    })}${editMode && html`<th class="pd-sticky-act"></th>`}</tr>`);

    // Build filter popovers (multiselect for all filterable columns)
    PROJ_COLS.forEach(c => {
      if ((c as { noFilter?: boolean }).noFilter) return;
      const pop = document.getElementById(`${prefix}-fp-${c.k}`);
      if (!pop) return;
      const current = projFilters[c.k];
      const checked: string[] = Array.isArray(current) ? current : [];
      const distinct = c.k === 'status'
        ? ['signoff', 'tuning', 'onboarding', 'live']
        : [...new Set(DATA.projects.map(p => String((p as unknown as Record<string, unknown>)[c.k] ?? '')).filter(Boolean))].sort();
      const label = (v: string) => c.k === 'status' ? v.charAt(0).toUpperCase() + v.slice(1) : v;
      setHtml(pop, distinct.length
        ? html`<div class="pd-pop-list">${distinct.map(v =>
          html`<label class="pd-pop-opt"><input type="checkbox" value="${v}" ${checked.includes(v) ? 'checked' : ''} onchange="window.__pd.setProjFilter('${c.k}','check',this)"/> ${label(v)}</label>`
        )}</div>
          <div class="pd-pop-actions"><button onclick="window.__pd.clearProjFilter('${c.k}')">Clear</button><button onclick="window.__pd.closeProjFilters()">Done</button></div>`
        : html`<div class="py-1.5 px-0.5 text-xs text-muted">No data</div>`);
      if (projFilterOpen === c.k) pop.classList.add('show');
    });

    // Filter rows
    let rows = DATA.projects.slice();
    Object.entries(projFilters).forEach(([k, v]) => {
      if (!v || (Array.isArray(v) && !v.length)) return;
      if (Array.isArray(v)) {
        rows = rows.filter(r => v.includes(String((r as unknown as Record<string, unknown>)[k] ?? '')));
      } else if (typeof v === 'string' && v.trim()) {
        const q = v.toLowerCase();
        rows = rows.filter(r => String((r as unknown as Record<string, unknown>)[k] ?? '').toLowerCase().includes(q));
      }
    });

    // Sort rows
    if (projSortKey) {
      const col = PROJ_COLS.find(c => c.k === projSortKey);
      rows.sort((a, b) => {
        let va: string | number = String((a as unknown as Record<string, unknown>)[projSortKey!] ?? '');
        let vb: string | number = String((b as unknown as Record<string, unknown>)[projSortKey!] ?? '');
        if ((col as { numeric?: boolean })?.numeric) {
          va = parseFloat(String(va).replace(/[^0-9.\-]/g, '')) || 0;
          vb = parseFloat(String(vb).replace(/[^0-9.\-]/g, '')) || 0;
        } else {
          va = va.toLowerCase(); vb = (vb as string).toLowerCase();
        }
        return (va < vb ? -1 : va > vb ? 1 : 0) * (projSortDir === 'asc' ? 1 : -1);
      });
    }

    const totalRows = rows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / projPageSize));
    if (projPage >= totalPages) projPage = totalPages - 1;
    const pageRows = rows.slice(projPage * projPageSize, (projPage + 1) * projPageSize);

    if (!totalRows) {
      setHtml(tbody, html`<tr><td colspan="${PROJ_COLS.length + (editMode ? 1 : 0)}" class="pd-empty">${DATA.projects.length ? 'No projects match the current filter' : `No projects yet${editMode ? ' — click "+ Add project" to add one' : ''}`}</td></tr>`);
    } else {
      setHtml(tbody, html`${pageRows.map(proj => {
        const cells = PROJ_COLS.map(c => renderProjCell(proj, c.k));
        const actionCell = editMode && html`<td class="pd-sticky-act" onclick="event.stopPropagation()"><div class="flex items-center gap-1"><button class="pd-row-btn" onclick="window.__pd.openModal('project',${jsArg(proj.id)})">Edit</button><button class="pd-row-btn pd-delete-btn" onclick="window.__pd.deleteItem('project',${jsArg(proj.id)})">Remove</button></div></td>`;
        return html`<tr class="pd-clickable" onclick="window.__pd.openDrawer(${jsArg(proj.id)})">${cells}${actionCell}</tr>`;
      })}`);
    }

    renderSectionFooter('proj-footer', totalRows, projPage, projPageSize, 'proj');
  }

  // ── ACTIONS ───────────────────────────────────────────────────────────────
  function actionRow(a: DashboardAction): SafeHtml {
    const proj = a.projectId && html`<span class="pd-atag pd-atag-proj">${projLabel(DATA, a.projectId)}</span>`;
    const archNote = a.archived && a.archivedAt && html`<span class="pd-atag">Archived</span>`;
    const editBtns = editMode && html`<div class="pd-arow-actions">
      <button class="pd-row-btn" onclick="window.__pd.openModal('action',${jsArg(a.id)})">Edit</button>
      ${!a.archived
        ? html`<button class="pd-row-btn pd-archive-btn" onclick="window.__pd.archiveItem('action',${jsArg(a.id)})">Archive</button>`
        : html`<button class="pd-row-btn" onclick="window.__pd.unarchiveItem('action',${jsArg(a.id)})">Restore</button>`}
      <button class="pd-row-btn pd-delete-btn" onclick="window.__pd.deleteItem('action',${jsArg(a.id)})">Delete</button>
    </div>`;

    let linkIcon: SafeHtml | null = null;
    if (a.linkedId) {
      const linked = DATA.decisions.find(d => d.id === a.linkedId);
      if (linked) {
        const lname = linked.name || linked.text || '';
        linkIcon = html`<span class="pd-link-icon" title="Linked to ${linked.type === 'risk' ? 'Risk' : 'Decision'}: ${lname}"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10 13a5 5 0 007 0l4-4a5 5 0 00-7-7l-1 1"/><path d="M14 11a5 5 0 00-7 0l-4 4a5 5 0 007 7l1-1"/></svg>${linked.type === 'risk' ? 'Risk' : 'Decision'}</span>`;
      }
    }

    const dueInfo = computeDueDisplay(a);
    const ownerLine = a.owner && html`<div class="text-2xs text-subtle font-medium uppercase tracking-label mt-1">Owner: ${a.owner}</div>`;
    const detailsLine = a.details && html`<div class="text-xs text-muted mt-1 leading-[1.4]">${a.details}</div>`;

    return html`<div class="pd-arow${a.archived ? ' pd-archived' : ''}">
      <div class="flex-1">
        <div class="pd-atext">${a.text}${linkIcon}</div>
        ${detailsLine}
        ${ownerLine}
        ${(proj || archNote) && html`<div class="flex flex-wrap gap-1 mt-1">${proj}${archNote}</div>`}
      </div>
      <span class="pd-due ${dueInfo.cls}" ${dueInfo.title && html`title="${dueInfo.title}"`}>${dueInfo.label}</span>
      ${editBtns}
    </div>`;
  }

  function renderActions() {
    const filterEl = p('action-filter');
    const tabsEl = p('ws-tabs');
    const panelsEl = p('action-panels');
    const addBtn = p('btn-add-action');
    if (!tabsEl || !panelsEl) return;

    if (filterEl) setHtml(filterEl, buildFilterDropdown(DATA, actionFilters, 'action', prefix));
    if (addBtn) addBtn.style.display = editMode ? 'inline-flex' : 'none';

    setHtml(tabsEl, html`${DATA.workstreams.map((ws, i) =>
      html`<div class="pd-tab${activeWS === i ? ' pd-tab-on' : ''}" onclick="window.__pd.switchWS(${i})">${ws}</div>`
    )}`);

    let activeTotal = 0;
    setHtml(panelsEl, html`${DATA.workstreams.map((ws, i) => {
      const all = DATA.actions.filter(a => a.ws === ws && (actionFilters.length === 0 || actionFilters.includes(a.projectId)));
      const active = all.filter(a => !a.archived);
      const archived = all.filter(a => a.archived);
      if (i === activeWS) activeTotal = active.length;
      const pageActive = i === activeWS
        ? active.slice(actionPage * actionPageSize, (actionPage + 1) * actionPageSize)
        : active;
      const rows = pageActive.length ? pageActive.map(a => actionRow(a)) : html`<div class="pd-empty">No actions in this workstream</div>`;
      const archBlock = archived.length > 0 && html`
        <div class="pd-arch-toggle" onclick="window.__pd.toggleArchived('actions')">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="${showArchivedActions ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}"/></svg>
          ${showArchivedActions ? 'Hide' : 'Show'} ${archived.length} archived
        </div>
        ${showArchivedActions && archived.map(a => actionRow(a))}`;
      return html`<div class="pd-action-list" style="display:${activeWS === i ? 'block' : 'none'}">${rows}${archBlock}</div>`;
    })}`);
    renderSectionFooter('action-footer', activeTotal, actionPage, actionPageSize, 'action');
  }

  // ── REPORTS ───────────────────────────────────────────────────────────────
  function reportRow(r: DashboardReport): SafeHtml {
    const proj = r.projectId && html`<div class="flex flex-wrap gap-1 mt-1"><span class="pd-atag pd-atag-proj">${projLabel(DATA, r.projectId)}</span></div>`;
    const editBtns = editMode && html`<div class="flex items-center gap-1 mt-1">
      <button class="pd-row-btn" onclick="window.__pd.openModal('report',${jsArg(r.id)})">Edit</button>
      ${!r.archived
        ? html`<button class="pd-row-btn pd-archive-btn" onclick="window.__pd.archiveItem('report',${jsArg(r.id)})">Archive</button>`
        : html`<button class="pd-row-btn" onclick="window.__pd.unarchiveItem('report',${jsArg(r.id)})">Restore</button>`}
      <button class="pd-row-btn pd-delete-btn" onclick="window.__pd.deleteItem('report',${jsArg(r.id)})">Delete</button>
    </div>`;
    const freqLine = [r.freq, r.audience].filter(Boolean).join(' — ');
    return html`<div class="pd-rrow${r.archived ? ' pd-archived' : ''}">
      <div>
        <div class="text-sm text-ink">${r.name}</div>
        <div class="text-xs text-muted italic mt-0.5">${freqLine}</div>
        ${proj}
        ${editBtns}
      </div>
      <div class="font-mono text-sm font-medium text-ink whitespace-nowrap">${fmtDate(r.date)}</div>
    </div>`;
  }

  function renderReports() {
    const filterEl = p('report-filter');
    const listEl = p('report-list');
    const addBtn = p('btn-add-report');
    if (!listEl) return;

    if (filterEl) setHtml(filterEl, buildFilterDropdown(DATA, reportFilters, 'report', prefix));
    if (addBtn) addBtn.style.display = editMode ? 'inline-flex' : 'none';

    const all = DATA.reports.filter(r => reportFilters.length === 0 || reportFilters.includes(r.projectId));
    const active = all.filter(r => !r.archived);
    const archived = all.filter(r => r.archived);
    const pageActive = active.slice(reportPage * reportPageSize, (reportPage + 1) * reportPageSize);

    setHtml(listEl, html`${pageActive.length ? pageActive.map(r => reportRow(r)) : html`<div class="pd-empty">No reports</div>`}${archived.length > 0 && html`
        <div class="pd-arch-toggle" onclick="window.__pd.toggleArchived('reports')">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="${showArchivedReports ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}"/></svg>
          ${showArchivedReports ? 'Hide' : 'Show'} ${archived.length} archived
        </div>
        ${showArchivedReports && archived.map(r => reportRow(r))}`}`);
    renderSectionFooter('report-footer', active.length, reportPage, reportPageSize, 'report');
  }

  // ── DECISIONS ─────────────────────────────────────────────────────────────
  function decisionRow(d: DashboardDecision): SafeHtml {
    const proj = d.projectId && html`<span class="pd-atag pd-atag-proj">${projLabel(DATA, d.projectId)}</span>`;
    const editBtns = editMode && html`<div class="flex items-center gap-1 ml-2">
      <button class="pd-row-btn" onclick="window.__pd.openModal('decision',${jsArg(d.id)})">Edit</button>
      ${!d.archived
        ? html`<button class="pd-row-btn pd-archive-btn" onclick="window.__pd.archiveItem('decision',${jsArg(d.id)})">Archive</button>`
        : html`<button class="pd-row-btn" onclick="window.__pd.unarchiveItem('decision',${jsArg(d.id)})">Restore</button>`}
      <button class="pd-row-btn pd-delete-btn" onclick="window.__pd.deleteItem('decision',${jsArg(d.id)})">Delete</button>
    </div>`;
    const displayName = d.name || d.text;
    return html`<div class="pd-drow${d.archived ? ' pd-archived' : ''}">
      <div class="pd-dicon">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="${d.type === 'risk' ? 'var(--color-danger)' : 'var(--color-warn)'}" stroke-width="2.5"><path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
      </div>
      <div class="flex-1">
        <div class="font-semibold text-ink text-sm">${displayName}</div>
        ${proj && html`<div class="flex flex-wrap gap-1 mt-1">${proj}</div>`}
      </div>
      <span class="pd-dlabel ${d.type === 'risk' ? 'pd-dl-risk' : 'pd-dl-dec'}">${d.type === 'risk' ? 'Risk' : 'Decision'}</span>
      ${editBtns}
    </div>`;
  }

  function renderDecisions() {
    const filterEl = p('decision-filter');
    const listEl = p('decision-list');
    const addBtn = p('btn-add-decision');
    if (!listEl) return;

    if (filterEl) setHtml(filterEl, buildFilterDropdown(DATA, decisionFilters, 'decision', prefix));
    if (addBtn) addBtn.style.display = editMode ? 'inline-flex' : 'none';

    const all = DATA.decisions.filter(d => decisionFilters.length === 0 || decisionFilters.includes(d.projectId));
    const active = all.filter(d => !d.archived);
    const archived = all.filter(d => d.archived);
    const pageActive = active.slice(decisionPage * decisionPageSize, (decisionPage + 1) * decisionPageSize);

    setHtml(listEl, html`${pageActive.length ? pageActive.map(d => decisionRow(d)) : html`<div class="pd-empty">No items</div>`}${archived.length > 0 && html`
        <div class="pd-arch-toggle" onclick="window.__pd.toggleArchived('decisions')">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="${showArchivedDecisions ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}"/></svg>
          ${showArchivedDecisions ? 'Hide' : 'Show'} ${archived.length} archived
        </div>
        ${showArchivedDecisions && archived.map(d => decisionRow(d))}`}`);
    renderSectionFooter('decision-footer', active.length, decisionPage, decisionPageSize, 'decision');
  }

  function renderAll() {
    renderRAG();
    renderProjects();
    renderActions();
    renderReports();
    renderDecisions();
  }

  // ── DRAWER ────────────────────────────────────────────────────────────────
  function openDrawer(id: string) {
    const proj = DATA.projects.find(x => x.id === id);
    if (!proj) return;
    const stages = ['signoff', 'tuning', 'onboarding', 'live'];
    const stageLabels: Record<string, string> = { signoff: 'Sign Off', tuning: 'Tuning', onboarding: 'Onboarding', live: 'Live' };
    const currentIdx = stages.indexOf(proj.status);
    const projActions = DATA.actions.filter(a => !a.archived && a.projectId === id);
    const projReports = DATA.reports.filter(r => !r.archived && r.projectId === id);
    const projDecisions = DATA.decisions.filter(d => !d.archived && d.projectId === id);
    const trend = proj.trend || [];
    const last7 = trend.slice(-7).reduce((a, b) => a + b, 0);
    const prev7 = trend.slice(-14, -7).reduce((a, b) => a + b, 0);
    const delta = prev7 ? Math.round(((last7 - prev7) / prev7) * 100) : 0;
    const drawerUsers = parseFloat(String(proj.users)) || 0;
    const drawerActual = parseFloat(String(proj.conversations)) || 0;
    const attainmentStat = proj.targetConvsPerUserPerWeek && drawerUsers ? (() => {
      const expected = proj.targetConvsPerUserPerWeek! * drawerUsers * (30 / 7);
      const ratio = drawerActual / expected;
      const pct = Math.round(ratio * 100);
      const color = ratio >= 0.9 ? 'var(--color-success)' : ratio >= 0.6 ? 'var(--color-warn)' : 'var(--color-danger)';
      const tip = `30-day attainment: ${proj.targetConvsPerUserPerWeek} convos/user/week × ${drawerUsers} users × (30÷7 days) = ${Math.round(expected)} expected. Actual: ${drawerActual}.`;
      return html`<div class="pd-dr-stat" title="${tip}"><div class="pd-dr-stat-l">Attainment</div><div class="pd-dr-stat-n" style="color:${color}">${pct}%</div><div class="pd-dr-stat-d">of ${Math.round(expected)} expected</div></div>`;
    })() : null;

    const drawerEl = p('drawer');
    if (!drawerEl) return;

    setHtml(drawerEl, html`
      <div class="pd-dr-hdr">
        <div>
          <div class="text-2xs tracking-[.12em] uppercase text-white/45 font-semibold mb-1.5">${proj.country} · ${proj.model} roll-out</div>
          <div class="text-[22px] font-semibold">${proj.brand}</div>
          <div class="mt-1.5 text-[12px] text-white/55 flex gap-3.5 flex-wrap items-center">
            <span>${STATUS_BADGES[proj.status] ?? proj.status}</span>
            ${proj.liveDate && proj.liveDate !== '—' && html`<span class="size-[3px] rounded-full bg-white/30"></span><span>Live since ${fmtDate(proj.liveDate)}</span>`}
            ${proj.leadName && html`<span class="size-[3px] rounded-full bg-white/30"></span><span>${proj.leadName}${proj.clientLead ? ' → ' + proj.clientLead : ''}</span>`}
          </div>
        </div>
        <button class="pd-dr-close" onclick="window.__pd.closeDrawer()">×</button>
      </div>
      <div class="pd-dr-body">
        <div class="pd-dr-stage">
          ${stages.map((s, i) => html`
            <div class="pd-dr-step${i < currentIdx ? ' done' : i === currentIdx ? ' current' : ''}">
              <div class="pd-dr-dot"></div><div class="pd-dr-step-lbl">${stageLabels[s]}</div>
            </div>`)}
        </div>
        ${proj.focus && html`<div class="pd-focus-box"><strong>Current focus</strong>${proj.focus}</div>`}
        <div class="pd-dr-stats">
          <div class="pd-dr-stat"><div class="pd-dr-stat-l">Users</div><div class="pd-dr-stat-n">${proj.users}</div></div>
          <div class="pd-dr-stat" title="${proj.activeUsers != null && drawerUsers > 0 ? `${proj.activeUsers} of ${drawerUsers} enabled users uploaded at least one conversation in the last 30 days` : 'Users who uploaded at least one conversation in the last 30 days'}"><div class="pd-dr-stat-l">Active</div><div class="pd-dr-stat-n">${proj.active}</div></div>
          <div class="pd-dr-stat"><div class="pd-dr-stat-l">Convos (30d)</div><div class="pd-dr-stat-n">${proj.conversations}</div>${trend.length > 0 && html`<div class="pd-dr-stat-d${delta >= 0 ? ' up' : ' dn'}" title="Week-on-week: last 7 days (${last7}) vs. previous 7 days (${prev7})">${delta >= 0 ? '▲' : '▼'} ${Math.abs(delta)}% wk/wk</div>`}</div>
          ${attainmentStat}
          <div class="pd-dr-stat"><div class="pd-dr-stat-l">Coaching</div><div class="pd-dr-stat-n">${proj.coaching}</div></div>
        </div>
        ${trend.length > 0 && html`<div class="pd-dr-section"><div class="pd-dr-section-hd">Conversation volume <span class="font-mono text-2xs text-subtle font-medium">last 15 days</span></div><div class="pd-dr-chart">${lineChart(trend)}</div></div>`}
        <div class="pd-dr-section">
          <div class="pd-dr-section-hd">Open actions <span class="font-mono text-2xs text-subtle font-medium">${projActions.length}</span></div>
          ${projActions.length ? projActions.map(a => html`
            <div class="pd-dr-list-row">
              <div class="flex-1"><div class="text-sm text-ink">${a.text}</div><div class="text-xs text-muted mt-0.5">${a.ws}</div></div>
              <span class="pd-due ${computeDueDisplay(a).cls}">${computeDueDisplay(a).label}</span>
            </div>`) : html`<div class="pd-dr-empty">No open actions linked to this project</div>`}
        </div>
        <div class="pd-dr-section">
          <div class="pd-dr-section-hd">Decisions &amp; risks <span class="font-mono text-2xs text-subtle font-medium">${projDecisions.length}</span></div>
          ${projDecisions.length ? projDecisions.map(d => html`
            <div class="pd-dr-list-row">
              <div class="flex-1"><div class="text-sm text-ink">${d.name || d.text}</div></div>
              <span class="pd-dlabel ${d.type === 'risk' ? 'pd-dl-risk' : 'pd-dl-dec'}">${d.type === 'risk' ? 'Risk' : 'Decision'}</span>
            </div>`) : html`<div class="pd-dr-empty">None open</div>`}
        </div>
        <div class="pd-dr-section">
          <div class="pd-dr-section-hd">Reporting cadence <span class="font-mono text-2xs text-subtle font-medium">${projReports.length}</span></div>
          ${projReports.length ? projReports.map(r => html`
            <div class="pd-dr-list-row">
              <div class="flex-1"><div class="text-sm text-ink">${r.name}</div><div class="text-xs text-muted mt-0.5">${r.freq}</div></div>
              <span class="pd-mono text-xs">${fmtDate(r.date)}</span>
            </div>`) : html`<div class="pd-dr-empty">No reports linked</div>`}
        </div>
        ${proj.lastTouch && html`<div class="text-xs text-muted text-right mt-2"><strong>Last touchpoint:</strong> ${proj.lastTouch}</div>`}
      </div>`);

    drawerEl.classList.add('show');
    const scrim = p('drawer-scrim');
    if (scrim) scrim.classList.add('show');
  }

  function closeDrawer() {
    const drawerEl = p('drawer');
    const scrim = p('drawer-scrim');
    drawerEl?.classList.remove('show');
    scrim?.classList.remove('show');
  }

  // ── PRESENTATION ──────────────────────────────────────────────────────────
  function openPresentation() {
    const c = RAG_CONFIG[DATA.meta.rag];
    const meetEl = p('meet');
    if (!meetEl) return;
    const subEl = p('meet-sub');
    if (subEl) setHtml(subEl, html`<span class="pd-meet-rag pd-meet-rag-${DATA.meta.rag[0]}">${c.label}</span>`);

    const activeProj = DATA.projects.filter(pp => pp.status === 'live' || pp.status === 'onboarding');
    const urgentActions = DATA.actions.filter(a => !a.archived && (a.dueType === 'soon' || a.dueType === 'decision')).slice(0, 5);
    const openDecisions = DATA.decisions.filter(d => !d.archived);
    const bodyEl = p('meet-body');
    if (bodyEl) setHtml(bodyEl, html`
      <div class="pd-mcard col-span-full">
        <div class="pd-mcard-hd">Programme status</div>
        <div class="pd-mcard-body">
          ${activeProj.map(pp => html`
            <div class="pd-mrow">
              <div><div class="text-sm font-medium text-white">${pp.brand}</div><div class="text-xs text-white/35 mt-[2px]">${pp.country} · ${pp.model} · ${STATUS_BADGES[pp.status]}</div></div>
              <div class="flex items-center gap-7">
                ${pp.conversations !== '—' && html`<div class="text-right"><div class="text-[24px] font-semibold text-white font-mono leading-none">${pp.conversations}</div><div class="text-[9px] uppercase tracking-eyebrow text-white/30 mt-[3px]">Conversations</div></div>`}
                ${pp.active && pp.active !== '—' && html`<div class="text-right"><div class="text-[24px] font-semibold text-white font-mono leading-none">${pp.active}</div><div class="text-[9px] uppercase tracking-eyebrow text-white/30 mt-[3px]">Active users</div></div>`}
              </div>
            </div>`)}
          ${activeProj.length === 0 && html`<div class="pd-meet-empty">No live or onboarding projects</div>`}
        </div>
      </div>
      <div class="pd-mcard">
        <div class="pd-mcard-hd">Priority actions</div>
        <div class="pd-mcard-body">
          ${urgentActions.length ? urgentActions.map(a => html`
            <div class="pd-mact-row"><div class="text-sm text-white/80">${a.text}</div><span class="pd-due pd-due-${a.dueType}">${a.due}</span></div>
          `) : html`<div class="pd-meet-empty">No urgent actions</div>`}
        </div>
      </div>
      <div class="pd-mcard">
        <div class="pd-mcard-hd">Decisions &amp; risks</div>
        <div class="pd-mcard-body">
          ${openDecisions.length ? openDecisions.map(d => html`
            <div class="pd-mact-row"><div class="text-sm text-white/80">${d.name || d.text}</div><span class="pd-dlabel ${d.type === 'risk' ? 'pd-dl-risk' : 'pd-dl-dec'}">${d.type === 'risk' ? 'Risk' : 'Decision'}</span></div>
          `) : html`<div class="pd-meet-empty">No open items</div>`}
        </div>
      </div>`);

    meetEl.classList.add('show');
  }

  function closePresentation() {
    p('meet')?.classList.remove('show');
  }

  // ── MODALS ────────────────────────────────────────────────────────────────
  function projOptions(): SafeHtml {
    return html`<option value="">— None —</option>${DATA.projects.slice().sort((a, b) => a.brand.localeCompare(b.brand)).map(pp =>
      html`<option value="${pp.id}">${pp.brand} (${pp.country})</option>`
    )}`;
  }

  function toggleDateField() {
    const t = (document.getElementById(`${prefix}-f-due`) as HTMLSelectElement)?.value;
    const d = document.getElementById(`${prefix}-f-due-date`) as HTMLInputElement | null;
    if (d) d.disabled = t !== 'date';
  }

  function renderWorkstreamRows() {
    const list = document.getElementById(`${prefix}-ws-list`);
    if (!list) return;
    setHtml(list, html`${DATA.workstreams.map((ws, i) =>
      html`<div class="pd-ws-row"><span class="text-subtle text-xs">${i + 1}.</span><input type="text" value="${ws}" class="pd-field-input" /><button class="pd-row-btn pd-delete-btn" onclick="this.closest('.pd-ws-row').remove()">Remove</button></div>`
    )}`);
  }

  function openModal(type: string, id: string | null = null) {
    modalCtx = type; modalEditId = id;
    const wrap = p('modal-wrap');
    const titleEl = p('modal-title');
    const bodyEl = p('modal-body');
    if (!wrap || !titleEl || !bodyEl) return;
    wrap.classList.add('show');
    modalDirty = false;

    if (type === 'action') {
      const item = id ? DATA.actions.find(a => a.id === id) : null;
      titleEl.textContent = id ? 'Edit action' : 'Add action';
      const linkOpts = html`<option value="">— None —</option>${DATA.decisions.map(d =>
        html`<option value="${d.id}"${item && item.linkedId === d.id ? ' selected' : ''}>${d.type === 'risk' ? 'Risk' : 'Decision'}: ${d.name || d.text}</option>`
      )}`;
      setHtml(bodyEl, html`
        <div class="pd-field"><label class="pd-field-label">Action text</label><textarea id="${prefix}-f-text" class="pd-field-input" rows="3">${item ? item.text : ''}</textarea></div>
        <div class="pd-field"><label class="pd-field-label">Details</label><textarea id="${prefix}-f-details" class="pd-field-input" rows="6" placeholder="Context, links, sub-tasks…">${item ? (item.details || '') : ''}</textarea></div>
        <div class="pd-field-row">
          <div class="pd-field"><label class="pd-field-label">Owner</label><input type="text" id="${prefix}-f-owner" class="pd-field-input" value="${item ? (item.owner || '') : ''}" placeholder="e.g. John Doe" /></div>
          <div class="pd-field"><label class="pd-field-label">Workstream</label><select id="${prefix}-f-ws" class="pd-field-input">${DATA.workstreams.map(ws => html`<option value="${ws}"${item && item.ws === ws ? ' selected' : ''}>${ws}</option>`)}</select></div>
        </div>
        <div class="pd-field-row">
          <div class="pd-field"><label class="pd-field-label">Due type</label><select id="${prefix}-f-due" class="pd-field-input" onchange="window.__pd.toggleDateField()">
            <option value="date"${(!item || item.dueType === 'date') ? ' selected' : ''}>Date</option>
            <option value="ongoing"${item && item.dueType === 'ongoing' ? ' selected' : ''}>Ongoing</option>
            <option value="decision"${item && item.dueType === 'decision' ? ' selected' : ''}>Decision required</option>
            <option value="tbc"${item && item.dueType === 'tbc' ? ' selected' : ''}>TBC</option>
          </select></div>
          <div class="pd-field"><label class="pd-field-label">Due date</label><input type="date" id="${prefix}-f-due-date" class="pd-field-input" value="${item ? (item.dueDate || '') : ''}" /></div>
        </div>
        <div class="pd-field"><label class="pd-field-label">Linked decision / risk (optional)</label><select id="${prefix}-f-linked" class="pd-field-input">${linkOpts}</select></div>
        <div class="pd-field"><label class="pd-field-label">Roll-out project (optional)</label><select id="${prefix}-f-proj" class="pd-field-input">${projOptions()}</select></div>`);
      const projSel = document.getElementById(`${prefix}-f-proj`) as HTMLSelectElement | null;
      if (projSel && item) projSel.value = item.projectId || '';
      setTimeout(toggleDateField, 0);
    } else if (type === 'report') {
      const item = id ? DATA.reports.find(r => r.id === id) : null;
      titleEl.textContent = id ? 'Edit report' : 'Add report';
      setHtml(bodyEl, html`
        <div class="pd-field"><label class="pd-field-label">Report name</label><input type="text" id="${prefix}-f-text" class="pd-field-input" value="${item ? item.name : ''}" /></div>
        <div class="pd-field-row">
          <div class="pd-field"><label class="pd-field-label">Frequency</label><input type="text" id="${prefix}-f-freq" class="pd-field-input" value="${item ? (item.freq || '') : ''}" placeholder="e.g. Fortnightly" /></div>
          <div class="pd-field"><label class="pd-field-label">Audience</label><input type="text" id="${prefix}-f-audience" class="pd-field-input" value="${item ? (item.audience || '') : ''}" placeholder="e.g. Rollout participants" /></div>
        </div>
        <div class="pd-field"><label class="pd-field-label">Next due date</label><input type="date" id="${prefix}-f-date" class="pd-field-input" value="${item && /^\d{4}-\d{2}-\d{2}$/.test(item.date || '') ? item.date : ''}" /></div>
        <div class="pd-field"><label class="pd-field-label">Roll-out project (optional)</label><select id="${prefix}-f-proj" class="pd-field-input">${projOptions()}</select></div>`);
      const projSel = document.getElementById(`${prefix}-f-proj`) as HTMLSelectElement | null;
      if (projSel && item) projSel.value = item.projectId || '';
    } else if (type === 'decision') {
      const item = id ? DATA.decisions.find(d => d.id === id) : null;
      titleEl.textContent = id ? 'Edit item' : 'Add decision / risk';
      setHtml(bodyEl, html`
        <div class="pd-field"><label class="pd-field-label">Name (short)</label><input type="text" id="${prefix}-f-name" class="pd-field-input" value="${item ? (item.name || '') : ''}" placeholder="e.g. AI Sales Coach" /></div>
        <div class="pd-field"><label class="pd-field-label">Description</label><textarea id="${prefix}-f-text" class="pd-field-input" rows="4">${item ? item.text : ''}</textarea></div>
        <div class="pd-field"><label class="pd-field-label">Decision / Mitigation</label><textarea id="${prefix}-f-mit" class="pd-field-input" rows="4" placeholder="The decision taken, or mitigation plan…">${item ? (item.mitigation || '') : ''}</textarea></div>
        <div class="pd-field"><label class="pd-field-label">Type</label><select id="${prefix}-f-type" class="pd-field-input">
          <option value="decision"${!item || item.type === 'decision' ? ' selected' : ''}>Decision required</option>
          <option value="risk"${item && item.type === 'risk' ? ' selected' : ''}>Risk</option>
        </select></div>
        <div class="pd-field"><label class="pd-field-label">Roll-out project (optional)</label><select id="${prefix}-f-proj" class="pd-field-input">${projOptions()}</select></div>`);
      const projSel = document.getElementById(`${prefix}-f-proj`) as HTMLSelectElement | null;
      if (projSel && item) projSel.value = item.projectId || '';
    } else if (type === 'project') {
      const item = id ? DATA.projects.find(pp => pp.id === id) : null;
      titleEl.textContent = id ? 'Edit project' : 'Add roll-out project';
      const kaiSelector = !id && kaiProjects.length > 0 && html`
        <div class="pd-field">
          <label class="pd-field-label">Import from KAI project</label>
          <select id="${prefix}-f-kai-proj" class="pd-field-input" onchange="window.__pd.fillFromKaiProject(this.value)">
            <option value="">— Select to pre-fill —</option>
            ${kaiProjects.slice().sort((a, b) => a.name.localeCompare(b.name)).map(kp => html`<option value="${kp.id}" data-name="${kp.name}" data-model="${kp.default_model}">${kp.name}</option>`)}
          </select>
        </div>`;
      setHtml(bodyEl, html`${kaiSelector}
        <div class="pd-field-row">
          <div class="pd-field"><label class="pd-field-label">Country</label><input type="text" id="${prefix}-f-country" class="pd-field-input" value="${item ? item.country : ''}" placeholder="e.g. US" /></div>
          <div class="pd-field"><label class="pd-field-label">Brand / project name</label><input type="text" id="${prefix}-f-brand" class="pd-field-input" value="${item ? item.brand : ''}" placeholder="e.g. Brand Name" /></div>
        </div>
        <div class="pd-field-row">
          <div class="pd-field"><label class="pd-field-label">Model</label><input type="text" id="${prefix}-f-model" class="pd-field-input" value="${item ? item.model : ''}" placeholder="e.g. Commercial" /></div>
          <div class="pd-field"><label class="pd-field-label">Status</label><select id="${prefix}-f-status" class="pd-field-input">
            ${['signoff', 'tuning', 'onboarding', 'live'].map(s => html`<option value="${s}"${item && item.status === s ? ' selected' : ''}>${s.charAt(0).toUpperCase() + s.slice(1)}</option>`)}
          </select></div>
        </div>
        <div class="pd-field-row">
          <div class="pd-field"><label class="pd-field-label">Live date</label><input type="date" id="${prefix}-f-livedate" class="pd-field-input" value="${item && /^\d{4}-\d{2}-\d{2}$/.test(item.liveDate || '') ? item.liveDate : ''}" /></div>
          <div class="pd-field"><label class="pd-field-label">Users</label><input type="text" id="${prefix}-f-users" class="pd-field-input" inputmode="numeric" value="${item?.users ?? ''}" placeholder="—" /></div>
        </div>
        <div class="pd-field-row">
          <div class="pd-field"><label class="pd-field-label">Coaching %</label><input type="text" id="${prefix}-f-coaching" class="pd-field-input" inputmode="numeric" value="${item?.coaching ?? ''}" placeholder="—" /></div>
          <div class="pd-field"><label class="pd-field-label">Target convos / user / week</label><input type="number" id="${prefix}-f-target" class="pd-field-input" step="0.1" min="0" value="${item?.targetConvsPerUserPerWeek ?? ''}" placeholder="e.g. 2" /></div>
        </div>
        <div class="pd-field"><label class="pd-field-label">Current focus</label><textarea id="${prefix}-f-focus" class="pd-field-input" placeholder="What this project is focused on…">${item ? (item.focus || '') : ''}</textarea></div>`);
    } else if (type === 'workstreams') {
      titleEl.textContent = 'Manage workstream labels';
      setHtml(bodyEl, html`
        <p class="text-xs text-muted mb-3">Add, rename, or remove workstream tabs. Removing a workstream moves its actions to the first remaining one.</p>
        <div id="${prefix}-ws-list"></div>
        <button class="pd-row-btn mt-1.5" onclick="window.__pd.addWorkstreamRow()">+ Add workstream</button>`);
      renderWorkstreamRows();
    }
  }

  function closeModal(force = false) {
    if (!force && modalDirty && !confirm('You have unsaved changes. Discard them?')) return;
    p('modal-wrap')?.classList.remove('show');
    modalCtx = null; modalEditId = null;
    modalDirty = false;
  }

  function saveModal() {
    const type = modalCtx;
    const id = modalEditId;
    const val = (elId: string) => (document.getElementById(`${prefix}-${elId}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)?.value || '';

    if (type === 'action') {
      const text = val('f-text').trim();
      if (!text) { alert('Please enter action text'); return; }
      const obj = {
        text, details: val('f-details').trim(), owner: val('f-owner').trim(),
        ws: val('f-ws'), dueType: val('f-due') as DashboardAction['dueType'],
        dueDate: val('f-due-date'), linkedId: val('f-linked'), projectId: val('f-proj'),
        due: val('f-due') === 'ongoing' ? 'Ongoing' : val('f-due') === 'decision' ? 'Decision req.' : val('f-due') === 'tbc' ? 'TBC' : '',
      };
      if (id) Object.assign(DATA.actions.find(a => a.id === id)!, obj);
      else DATA.actions.push({ id: uid(), ...obj, archived: false, archivedAt: null });
    } else if (type === 'report') {
      const name = val('f-text').trim();
      if (!name) { alert('Please enter report name'); return; }
      const obj = { name, freq: val('f-freq').trim(), audience: val('f-audience').trim(), date: val('f-date'), projectId: val('f-proj') };
      if (id) Object.assign(DATA.reports.find(r => r.id === id)!, obj);
      else DATA.reports.push({ id: uid(), ...obj, archived: false, archivedAt: null });
    } else if (type === 'decision') {
      const name = val('f-name').trim();
      const text = val('f-text').trim();
      if (!name && !text) { alert('Please enter at least a name or description'); return; }
      const obj = { name, text, mitigation: val('f-mit').trim(), type: val('f-type') as 'decision' | 'risk', projectId: val('f-proj') };
      if (id) Object.assign(DATA.decisions.find(d => d.id === id)!, obj);
      else DATA.decisions.push({ id: uid(), ...obj, archived: false, archivedAt: null });
    } else if (type === 'project') {
      const country = val('f-country').trim();
      const brand = val('f-brand').trim();
      if (!brand || !country) { alert('Country and brand are required'); return; }
      const targetStr = val('f-target').trim();
      const obj = {
        country, brand, model: val('f-model').trim(),
        status: val('f-status') as DashboardProject['status'],
        liveDate: val('f-livedate').trim(), users: val('f-users').trim(),
        coaching: val('f-coaching').trim(), focus: val('f-focus').trim(),
        targetConvsPerUserPerWeek: targetStr ? parseFloat(targetStr) || undefined : undefined,
      };
      if (id) {
        const pp = DATA.projects.find(pp => pp.id === id)!;
        Object.assign(pp, obj);
        if (pp.kaiProjectId) fetchMetrics([pp], false);
      } else {
        const newProj: DashboardProject = { id: uid(), ...obj, conversations: 0, totalConversations: 0, active: '', trend: [], kaiProjectId: val('f-kai-proj') || undefined };
        DATA.projects.push(newProj);
        if (newProj.kaiProjectId) fetchMetrics([newProj]);
      }
    } else if (type === 'workstreams') {
      const newList = [...document.querySelectorAll(`#${prefix}-ws-list .pd-ws-row input`)].map(i => (i as HTMLInputElement).value.trim()).filter(Boolean);
      if (!newList.length) { alert('At least one workstream is required'); return; }
      DATA.actions.forEach(a => { if (!newList.includes(a.ws)) a.ws = newList[0]; });
      DATA.workstreams = newList;
      if (activeWS >= newList.length) activeWS = 0;
    }

    save();
    closeModal(true);
    renderAll();
  }

  // ── PUBLIC API (exposed on window for inline onclick handlers) ─────────────
  (window as unknown as Record<string, unknown>).__pd = {
    setRAG: (val: string) => {
      if (!editMode) return;
      DATA.meta.rag = val as DashboardContent['meta']['rag'];
      save(); renderRAG();
    },
    switchWS: (i: number) => { activeWS = i; actionPage = 0; renderActions(); },
    openSectionFilter: (section: string, e: Event) => {
      e.stopPropagation();
      const pop = document.getElementById(`${prefix}-fpop-${section}`);
      if (!pop) return;
      const wasOpen = pop.classList.contains('show');
      ['action', 'report', 'decision'].forEach(s =>
        document.getElementById(`${prefix}-fpop-${s}`)?.classList.remove('show'));
      filterDropOpen = null;
      if (!wasOpen) { pop.classList.add('show'); filterDropOpen = section; }
    },
    toggleSectionFilter: (section: string, projectId: string) => {
      const arr = section === 'action' ? actionFilters : section === 'report' ? reportFilters : decisionFilters;
      const idx = arr.indexOf(projectId);
      if (idx === -1) arr.push(projectId); else arr.splice(idx, 1);
      if (section === 'action') { actionPage = 0; renderActions(); }
      else if (section === 'report') { reportPage = 0; renderReports(); }
      else { decisionPage = 0; renderDecisions(); }
    },
    clearSectionFilter: (section: string) => {
      if (section === 'action') { actionFilters = []; actionPage = 0; renderActions(); }
      else if (section === 'report') { reportFilters = []; reportPage = 0; renderReports(); }
      else { decisionFilters = []; decisionPage = 0; renderDecisions(); }
    },
    toggleArchived: (type: string) => {
      if (type === 'actions') showArchivedActions = !showArchivedActions;
      if (type === 'reports') showArchivedReports = !showArchivedReports;
      if (type === 'decisions') showArchivedDecisions = !showArchivedDecisions;
      renderAll();
    },
    archiveItem: (type: string, id: string) => {
      const col = type === 'action' ? DATA.actions : type === 'report' ? DATA.reports : DATA.decisions;
      const item = col.find((x: { id: string }) => x.id === id) as { archived: boolean; archivedAt: string | null } | undefined;
      if (!item) return;
      item.archived = true; item.archivedAt = new Date().toISOString();
      save(); renderAll();
    },
    unarchiveItem: (type: string, id: string) => {
      const col = type === 'action' ? DATA.actions : type === 'report' ? DATA.reports : DATA.decisions;
      const item = col.find((x: { id: string }) => x.id === id) as { archived: boolean; archivedAt: string | null } | undefined;
      if (!item) return;
      item.archived = false; item.archivedAt = null;
      save(); renderAll();
    },
    deleteItem: (type: string, id: string) => {
      if (!confirm('Permanently delete this item? This cannot be undone.')) return;
      if (type === 'project') DATA.projects = DATA.projects.filter(x => x.id !== id);
      else if (type === 'action') DATA.actions = DATA.actions.filter(x => x.id !== id);
      else if (type === 'report') DATA.reports = DATA.reports.filter(x => x.id !== id);
      else if (type === 'decision') DATA.decisions = DATA.decisions.filter(x => x.id !== id);
      save(); renderAll();
    },
    openModal, closeModal, saveModal,
    openDrawer, closeDrawer,
    openPresentation, closePresentation,
    toggleDateField,
    fillFromKaiProject: (kaiId: string) => {
      const kp = kaiProjects.find(k => k.id === kaiId);
      if (!kp) return;
      const set = (elId: string, val: string) => {
        const el = document.getElementById(`${prefix}-${elId}`) as HTMLInputElement | null;
        if (el) el.value = val;
      };
      set('f-brand', kp.name);
      set('f-model', kp.default_model || '');
      if (kp.default_language) set('f-country', langToCountry(kp.default_language));
    },
    addWorkstreamRow: () => {
      const list = document.getElementById(`${prefix}-ws-list`);
      if (!list) return;
      const div = document.createElement('div'); div.className = 'pd-ws-row';
      setHtml(div, html`<span class="text-subtle text-xs">•</span><input type="text" value="" placeholder="New workstream" class="pd-field-input" /><button class="pd-row-btn pd-delete-btn" onclick="this.closest('.pd-ws-row').remove()">Remove</button>`);
      list.appendChild(div); (div.querySelector('input') as HTMLInputElement).focus();
    },
    setPage: (type: string, n: number) => {
      if (type === 'proj') { projPage = n; renderProjects(); }
      else if (type === 'action') { actionPage = n; renderActions(); }
      else if (type === 'report') { reportPage = n; renderReports(); }
      else if (type === 'decision') { decisionPage = n; renderDecisions(); }
    },
    setPageSize: (type: string, n: number) => {
      if (type === 'proj') { projPageSize = n; projPage = 0; renderProjects(); }
      else if (type === 'action') { actionPageSize = n; actionPage = 0; renderActions(); }
      else if (type === 'report') { reportPageSize = n; reportPage = 0; renderReports(); }
      else if (type === 'decision') { decisionPageSize = n; decisionPage = 0; renderDecisions(); }
    },
    sortProj: (key: string, e: Event) => {
      e.stopPropagation();
      if (projSortKey === key) projSortDir = projSortDir === 'asc' ? 'desc' : 'asc';
      else { projSortKey = key; projSortDir = 'asc'; }
      projPage = 0;
      renderProjects();
    },
    toggleProjFilter: (key: string, e: Event) => {
      e.stopPropagation();
      const pop = document.getElementById(`${prefix}-fp-${key}`);
      if (!pop) return;
      const isOpen = pop.classList.contains('show');
      closeProjFilterPops();
      if (!isOpen) {
        projFilterOpen = key;
        const th = (e.target as HTMLElement).closest('th');
        if (th) {
          const rect = th.getBoundingClientRect();
          pop.style.top = (rect.bottom + 2) + 'px';
          pop.style.left = rect.left + 'px';
        }
        pop.classList.add('show');
      }
    },
    setProjFilter: (key: string, mode: string, el: HTMLInputElement) => {
      if (mode === 'check') {
        const checked: string[] = Array.isArray(projFilters[key]) ? [...projFilters[key] as string[]] : [];
        if (el.checked) { if (!checked.includes(el.value)) checked.push(el.value); }
        else { const i = checked.indexOf(el.value); if (i !== -1) checked.splice(i, 1); }
        if (checked.length) projFilters[key] = checked; else delete projFilters[key];
      } else {
        const v = el.value.trim();
        if (v) projFilters[key] = v; else delete projFilters[key];
      }
      projPage = 0;
      renderProjects();
    },
    clearProjFilter: (key: string) => { delete projFilters[key]; projPage = 0; renderProjects(); },
    closeProjFilters: () => closeProjFilterPops(),
  };

  // close modal on outside click (with dirty check)
  const modalWrap = p('modal-wrap');
  if (modalWrap) modalWrap.addEventListener('click', (e) => { if (e.target === modalWrap) closeModal(); });

  // mark modal dirty whenever a field is touched
  const modalBody = p('modal-body');
  if (modalBody) {
    modalBody.addEventListener('input', () => { modalDirty = true; });
    modalBody.addEventListener('change', () => { modalDirty = true; });
  }

  // close filter popovers on outside click (bubble phase — pops use stopPropagation internally)
  document.addEventListener('click', () => {
    if (projFilterOpen) closeProjFilterPops();
    if (filterDropOpen) {
      document.getElementById(`${prefix}-fpop-${filterDropOpen}`)?.classList.remove('show');
      filterDropOpen = null;
    }
  });

  renderAll();

  return {
    setData: (d: DashboardContent) => { DATA = JSON.parse(JSON.stringify(d)); renderAll(); },
    setEditMode: (enabled: boolean) => {
      editMode = enabled && opts.editAllowed;
      const addBtns = ['btn-add-action', 'btn-add-report', 'btn-add-decision', 'btn-add-project'];
      addBtns.forEach(id => { const el = p(id); if (el) el.style.display = editMode ? 'inline-flex' : 'none'; });
      renderAll();
    },
    getEditMode: () => editMode,
    getData: () => JSON.parse(JSON.stringify(DATA)),
  };
}
